import Cookies from 'js-cookie';
import {
  addClasses,
  loadCSS,
  removeClasses,
  onLocationChangeListener,
} from './DOMHelpers';
import {
  body,
  widgetHolder,
  createBubbleHolder,
  createBubbleIcon,
  bubbleSVG,
  chatBubble,
  closeBubble,
  bubbleHolder,
  onClickChatBubble,
  onBubbleClick,
  setBubbleText,
  addUnreadClass,
  removeUnreadClass,
} from './bubbleHelpers';
import { isWidgetColorLighter } from 'shared/helpers/colorHelper';
import { dispatchWindowEvent } from 'shared/helpers/CustomEventHelper';
import { CLOUDCHAT_ERROR, CLOUDCHAT_READY } from '../widget/constants/sdkEvents';
import { SET_USER_ERROR } from '../widget/constants/errorTypes';
import {
  getUserCookieName,
  removeCookieWithDomain,
  setCookieWithDomain,
} from './cookieHelpers';
import { getForwardedAuthToken } from './forwardedAuthToken';
import {
  getAlertAudio,
  initOnEvents,
} from 'shared/helpers/AudioNotificationHelper';
import { isFlatWidgetStyle } from './settingsHelper';
import { SDK_SET_AUTH_TOKEN } from 'shared/constants/sharedFrameEvents';
import { popoutChatWindow } from '../widget/helpers/popoutHelper';
import addHours from 'date-fns/addHours';

// Bounds stale-session recovery. It has to be bounded somewhere on THIS side and not in the widget
// bundle: recovery reloads the iframe, so a counter kept in that bundle is reset by the very reload it
// is meant to bound. An unbounded reload-on-410 is a reload loop, and each iteration boots a fresh
// contact_inbox — the exact table this work exists to stop growing.
//
// A rate limit rather than the once-per-page flag it replaces, for two reasons learned the hard way:
//
// - once-per-page is spent by the BOOT. A tab that opens holding a token whose row is already gone
//   410s during boot and recovers immediately — which is correct, and which consumed the entire
//   budget before the visitor typed anything. Every later death in that page life was then refused,
//   leaving the tab on a failed message with no way out but a manual reload: the exact failure this
//   mechanism exists to remove. Measured in a real browser against instance 4, not theorised.
//   The budget therefore has to cover the boot AND the session's own life.
// - the state has to be READABLE in production. The flag this replaces lived in a module-scoped `let`,
//   and when recovery silently stopped happening there was nothing to inspect: same symptom for "guard
//   already spent", "no frame", and "handler never ran". Living on window.$cloudchat it can be read
//   from any console, and every suppression says which limit it hit.
//
// Fail OPEN whenever the state is unreadable: an extra reload costs a visible blink, a wrong
// suppression costs a widget that looks alive and never receives again.
// Three, not two: one is routinely spent by the boot-time recovery described above, so a smaller
// ceiling leaves at most one for everything the visitor actually does on the page.
const MAX_STALE_RECOVERIES_PER_PAGE = 3;
const STALE_RECOVERY_MIN_INTERVAL_MS = 5000;

const staleRecoveryState = () => {
  const sdk = window.$cloudchat;
  if (!sdk) {
    return null;
  }
  if (!sdk.staleRecovery) {
    sdk.staleRecovery = { count: 0, lastAt: 0 };
  }
  return sdk.staleRecovery;
};

// Origin the widget iframe is served from. Returns null when baseUrl is not a
// valid absolute URL, so callers can fail closed instead of falling back to '*'.
const getWidgetOrigin = () => {
  try {
    return new URL(window.$cloudchat?.baseUrl).origin;
  } catch {
    return null;
  }
};

const updateAuthCookie = (cookieContent, baseDomain = '') =>
  setCookieWithDomain('cw_conversation', cookieContent, {
    baseDomain,
  });

const updateCampaignReadStatus = baseDomain => {
  const expireBy = addHours(new Date(), 1);
  setCookieWithDomain('cw_snooze_campaigns_till', Number(expireBy), {
    expires: expireBy,
    baseDomain,
  });
};

export const IFrameHelper = {
  adoptSiblingSession: (iframe, { baseUrl, websiteToken }) => {
    if (document.visibilityState !== 'visible') return;
    if (!window.$cloudchat?.sessionlessBoot || window.$cloudchat.isOpen) return;
    const siblingCookie = Cookies.get('cw_conversation');
    if (!siblingCookie || siblingCookie === window.$cloudchat.bootCookie)
      return;
    window.$cloudchat.sessionlessBoot = false;
    window.$cloudchat.bootCookie = siblingCookie;
    window.$cloudchat.resetTriggered = true;
    iframe.src = `${IFrameHelper.getUrl({
      baseUrl,
      websiteToken,
    })}&cw_conversation=${siblingCookie}`;
  },
  getUrl({ baseUrl, websiteToken }) {
    return `${baseUrl}/widget?website_token=${websiteToken}&lazy_session=1`;
  },
  destroy: () => {
    const iframe = document.getElementById('chatwoot_live_chat_widget');
    const holder = document.getElementById('cw-widget-holder');
    const bubble = document.getElementById('cw-bubble-holder');
    const stylesEl = document.getElementById('cw-widget-styles');
    [iframe, holder, bubble, stylesEl].forEach(el => {
      if (el && el.parentNode) {
        el.parentNode.removeChild(el);
      }
    });
    window.$cloudchat = null;
  },
  createFrame: ({ baseUrl, websiteToken }) => {
    if (IFrameHelper.getAppFrame()) {
      return;
    }

    loadCSS();
    const iframe = document.createElement('iframe');
    const cwCookie = Cookies.get('cw_conversation');
    let widgetUrl = IFrameHelper.getUrl({ baseUrl, websiteToken });
    if (cwCookie) {
      widgetUrl = `${widgetUrl}&cw_conversation=${cwCookie}`;
    }
    iframe.src = widgetUrl;
    window.$cloudchat.bootCookie = cwCookie || null;
    const adoptSiblingSession = () =>
      IFrameHelper.adoptSiblingSession(iframe, { baseUrl, websiteToken });
    document.addEventListener('visibilitychange', adoptSiblingSession);
    window.addEventListener('focus', adoptSiblingSession);
    iframe.allow =
      'camera;microphone;fullscreen;display-capture;picture-in-picture;clipboard-write;';
    iframe.id = 'chatwoot_live_chat_widget';
    iframe.style.visibility = 'hidden';

    let holderClassName = `woot-widget-holder woot--hide woot-elements--${window.$cloudchat.position}`;
    if (window.$cloudchat.hideMessageBubble) {
      holderClassName += ` woot-widget--without-bubble`;
    }
    if (isFlatWidgetStyle(window.$cloudchat.widgetStyle)) {
      holderClassName += ` woot-widget-holder--flat`;
    }

    addClasses(widgetHolder, holderClassName);
    widgetHolder.id = 'cw-widget-holder';
    widgetHolder.appendChild(iframe);
    body.appendChild(widgetHolder);
    IFrameHelper.initPostMessageCommunication();
    IFrameHelper.initWindowSizeListener();
    IFrameHelper.preventDefaultScroll();
  },
  getAppFrame: () => document.getElementById('chatwoot_live_chat_widget'),
  getBubbleHolder: () => document.getElementsByClassName('woot--bubble-holder'),
  sendMessage: (key, value) => {
    const element = IFrameHelper.getAppFrame();
    element?.contentWindow.postMessage(
      `cloudchat-widget:${JSON.stringify({ event: key, ...value })}`,
      '*'
    );
  },
  // Origin-pinned sibling of `sendMessage`. `sendMessage` posts to '*' and is
  // shared by a dozen commands; a credential must only ever reach the widget
  // origin, so an unparseable baseUrl drops the message instead of widening the
  // target.
  sendMessageToWidgetOrigin: (key, value) => {
    const targetOrigin = getWidgetOrigin();
    if (!targetOrigin) {
      return;
    }
    IFrameHelper.getAppFrame()?.contentWindow.postMessage(
      `cloudchat-widget:${JSON.stringify({ event: key, ...value })}`,
      targetOrigin
    );
  },
  initPostMessageCommunication: () => {
    window.onmessage = e => {
      if (
        typeof e.data !== 'string' ||
        e.data.indexOf('cloudchat-widget:') !== 0
      ) {
        return;
      }
      // Only the widget itself drives these events. Enforced only when the origin
      // is resolvable — an unparseable baseUrl must not silently disable the whole
      // widget, and no credential travels in this direction.
      const widgetOrigin = getWidgetOrigin();
      if (widgetOrigin && e.origin !== widgetOrigin) {
        return;
      }
      const message = JSON.parse(e.data.replace('cloudchat-widget:', ''));
      if (typeof IFrameHelper.events[message.event] === 'function') {
        IFrameHelper.events[message.event](message);
      }
    };
  },
  initWindowSizeListener: () => {
    window.addEventListener('resize', () => IFrameHelper.toggleCloseButton());
  },
  preventDefaultScroll: () => {
    widgetHolder.addEventListener('wheel', event => {
      const deltaY = event.deltaY;
      const contentHeight = widgetHolder.scrollHeight;
      const visibleHeight = widgetHolder.offsetHeight;
      const scrollTop = widgetHolder.scrollTop;

      if (
        (scrollTop === 0 && deltaY < 0) ||
        (visibleHeight + scrollTop === contentHeight && deltaY > 0)
      ) {
        event.preventDefault();
      }
    });
  },

  setFrameHeightToFitContent: (extraHeight, isFixedHeight) => {
    const iframe = IFrameHelper.getAppFrame();
    const updatedIframeHeight = isFixedHeight ? `${extraHeight}px` : '100%';

    if (iframe)
      iframe.setAttribute('style', `height: ${updatedIframeHeight} !important`);
  },

  setupAudioListeners: () => {
    const { baseUrl = '' } = window.$cloudchat;
    getAlertAudio(baseUrl, { type: 'widget', alertTone: 'ding' }).then(() =>
      initOnEvents.forEach(event => {
        document.removeEventListener(
          event,
          IFrameHelper.setupAudioListeners,
          false
        );
      })
    );
  },

  events: {
    loaded: message => {
      window.$cloudchat.sessionlessBoot = !message.config.authToken;
      const { bootCookie } = window.$cloudchat;
      if (!message.config.authToken && bootCookie) {
        if (Cookies.get('cw_conversation') === bootCookie) {
          removeCookieWithDomain(
            'cw_conversation',
            window.$cloudchat.baseDomain
          );
        }
        window.$cloudchat.bootCookie = null;
      }
      if (message.config.authToken) {
        updateAuthCookie(
          message.config.authToken,
          window.$cloudchat.baseDomain
        );
      }
      window.$cloudchat.hasLoaded = true;
      const campaignsSnoozedTill = Cookies.get('cw_snooze_campaigns_till');

      IFrameHelper.sendMessage('config-set', {
        locale: window.$cloudchat.locale,
        position: window.$cloudchat.position,
        hideMessageBubble: window.$cloudchat.hideMessageBubble,
        showPopoutButton: window.$cloudchat.showPopoutButton,
        widgetStyle: window.$cloudchat.widgetStyle,
        darkMode: window.$cloudchat.darkMode,
        showUnreadMessagesDialog: window.$cloudchat.showUnreadMessagesDialog,
        multipleConversations:
          message.config.channelConfig.multipleConversations,
        campaignsSnoozedTill,
      });

      IFrameHelper.onLoad({
        widgetColor: message.config.channelConfig.widgetColor,
      });

      IFrameHelper.toggleCloseButton();

      // Replays the host's identify onto a freshly loaded frame, so an iframe reload (reset(), or
      // recoverStaleSession below) does not silently drop the visitor's identity.
      //
      // The `{ identifier, user }` shape is load-bearing: sendMessage builds `{ event, ...value }`
      // and the widget store destructures `{ identifier, user: userObject }`. This used to pass
      // `window.$cloudchat.user` alone — which carries no identifier, since setUser keeps it in
      // `window.$cloudchat.identifier` — so `userObject` arrived undefined and the destructuring
      // threw. The action's own try/catch swallowed it into a SET_USER_ERROR event, and this file's
      // `error` handler answers that by removing the user cookie. Net effect of the old shape: every
      // widget load with a stored user failed to re-identify AND wiped the identify-dedup cookie.
      if (window.$cloudchat.user) {
        IFrameHelper.sendMessage('set-user', {
          identifier: window.$cloudchat.identifier,
          user: window.$cloudchat.user,
        });
      }

      // The host page can call setAuthToken before the widget is listening, so
      // replay it here — otherwise the first message of the session would go out
      // without the credential.
      const forwardedAuthToken = getForwardedAuthToken();
      if (forwardedAuthToken) {
        IFrameHelper.sendMessageToWidgetOrigin(SDK_SET_AUTH_TOKEN, {
          token: forwardedAuthToken,
        });
      }

      window.playAudioAlert = () => {};

      initOnEvents.forEach(e => {
        document.addEventListener(e, IFrameHelper.setupAudioListeners, false);
      });

      if (!window.$cloudchat.resetTriggered) {
        dispatchWindowEvent({ eventName: CLOUDCHAT_READY });
      }
    },
    error: ({ errorType, data }) => {
      dispatchWindowEvent({ eventName: CLOUDCHAT_ERROR, data: data });

      if (errorType === SET_USER_ERROR) {
        removeCookieWithDomain(
          getUserCookieName(),
          window.$cloudchat.baseDomain
        );
      }
    },
    onEvent({ eventIdentifier: eventName, data }) {
      dispatchWindowEvent({ eventName, data });
    },
    setBubbleLabel(message) {
      setBubbleText(window.$cloudchat.launcherTitle || message.label);
    },

    setAuthCookie({ data: { widgetAuthToken } }) {
      window.$cloudchat.sessionlessBoot = false;
      updateAuthCookie(widgetAuthToken, window.$cloudchat.baseDomain);
    },

    // The widget got a 410 stale_widget_session: the JWT it holds points at a contact_inbox that no
    // longer exists. The iframe boot (WidgetsController) is the only session-minting path a stranded
    // tab can reach — set_user mints one too, but only when the HOST calls setUser with a different
    // identifier, which the widget cannot trigger for itself. So reload the frame and let boot do it;
    // the widget cannot reload itself out of its own document, which is why it asks us.
    //
    // Deliberately NOT window.$cloudchat.reset(): that closes an open widget and removes the user
    // cookie, throwing away the host's identify state. Here we only need a fresh session token, and
    // the `loaded` handler above replays set-user so an identified visitor stays identified.
    //
    // getUrl() carries no cw_conversation, so boot starts clean instead of re-presenting the dead
    // token; the cookie is overwritten by updateAuthCookie when the new frame reports `loaded`.
    recoverStaleSession() {
      // Frame check FIRST: no frame means nothing to reload, and that must not consume the budget.
      const iframe = IFrameHelper.getAppFrame();
      if (!iframe) {
        return;
      }

      const state = staleRecoveryState();
      const now = Date.now();
      if (state) {
        if (state.count >= MAX_STALE_RECOVERIES_PER_PAGE) {
          // eslint-disable-next-line no-console
          console.warn(
            `[cloudchat] stale session recovery suppressed: already recovered ${state.count} time(s) on this page`
          );
          return;
        }
        const sinceLast = now - state.lastAt;
        if (state.count > 0 && sinceLast < STALE_RECOVERY_MIN_INTERVAL_MS) {
          // A repeat this soon is the freshly booted session failing the same way, not a new death.
          // eslint-disable-next-line no-console
          console.warn(
            `[cloudchat] stale session recovery suppressed: ${sinceLast}ms since the last one`
          );
          return;
        }
        state.count += 1;
        state.lastAt = now;
      }

      // Same reason reset() sets it: the reloaded frame reports `loaded` again, and CLOUDCHAT_READY is
      // dispatched from there. Without this the host page receives a second ready event for a session
      // it never asked to restart, re-running whatever it hangs off that event.
      window.$cloudchat.resetTriggered = true;
      window.$cloudchat.bootCookie = null;

      iframe.src = IFrameHelper.getUrl({
        baseUrl: window.$cloudchat.baseUrl,
        websiteToken: window.$cloudchat.websiteToken,
      });
    },

    setCampaignReadOn() {
      updateCampaignReadStatus(window.$cloudchat.baseDomain);
    },

    toggleBubble: state => {
      let bubbleState = {};
      if (state === 'open') {
        bubbleState.toggleValue = true;
      } else if (state === 'close') {
        bubbleState.toggleValue = false;
      }

      onBubbleClick(bubbleState);
    },

    popoutChatWindow: ({ baseUrl, websiteToken, locale }) => {
      const cwCookie = Cookies.get('cw_conversation');
      window.$cloudchat.toggle('close');
      popoutChatWindow(baseUrl, websiteToken, locale, cwCookie);
    },

    closeWindow: () => {
      onBubbleClick({ toggleValue: false });
      removeUnreadClass();
    },

    onBubbleToggle: isOpen => {
      IFrameHelper.sendMessage('toggle-open', { isOpen });
      if (isOpen) {
        IFrameHelper.pushEvent('webwidget.triggered');
      }
    },
    onLocationChange: ({ referrerURL, referrerHost }) => {
      IFrameHelper.sendMessage('change-url', {
        referrerURL,
        referrerHost,
      });
    },
    updateIframeHeight: message => {
      const { extraHeight = 0, isFixedHeight } = message;

      IFrameHelper.setFrameHeightToFitContent(extraHeight, isFixedHeight);
    },

    setUnreadMode: () => {
      addUnreadClass();
      onBubbleClick({ toggleValue: true });
    },

    resetUnreadMode: () => removeUnreadClass(),
    handleNotificationDot: event => {
      if (window.$cloudchat.hideMessageBubble) {
        return;
      }

      const bubbleElement = document.querySelector('.woot-widget-bubble');
      if (
        event.unreadMessageCount > 0 &&
        !bubbleElement.classList.contains('unread-notification')
      ) {
        addClasses(bubbleElement, 'unread-notification');
      } else if (event.unreadMessageCount === 0) {
        removeClasses(bubbleElement, 'unread-notification');
      }
    },

    closeChat: () => {
      onBubbleClick({ toggleValue: false });
    },

    playAudio: () => {
      window.playAudioAlert();
    },
  },
  pushEvent: eventName => {
    IFrameHelper.sendMessage('push-event', { eventName });
  },

  onLoad: ({ widgetColor }) => {
    const iframe = IFrameHelper.getAppFrame();
    iframe.style.visibility = '';
    iframe.setAttribute('id', `chatwoot_live_chat_widget`);

    if (IFrameHelper.getBubbleHolder().length) {
      return;
    }
    createBubbleHolder(window.$cloudchat.hideMessageBubble);
    onLocationChangeListener();

    let className = 'woot-widget-bubble';
    let closeBtnClassName = `woot-elements--${window.$cloudchat.position} woot-widget-bubble woot--close woot--hide`;

    if (isFlatWidgetStyle(window.$cloudchat.widgetStyle)) {
      className += ' woot-widget-bubble--flat';
      closeBtnClassName += ' woot-widget-bubble--flat';
    }

    if (isWidgetColorLighter(widgetColor)) {
      className += ' woot-widget-bubble-color--lighter';
      closeBtnClassName += ' woot-widget-bubble-color--lighter';
    }

    const chatIcon = createBubbleIcon({
      className,
      path: bubbleSVG,
      target: chatBubble,
    });

    addClasses(closeBubble, closeBtnClassName);

    chatIcon.style.background = widgetColor;
    closeBubble.style.background = widgetColor;

    bubbleHolder.appendChild(chatIcon);
    bubbleHolder.appendChild(closeBubble);
    onClickChatBubble();
  },
  toggleCloseButton: () => {
    let isMobile = false;
    if (window.matchMedia('(max-width: 668px)').matches) {
      isMobile = true;
    }
    IFrameHelper.sendMessage('toggle-close-button', { isMobile });
  },
};
